import { Component, DestroyRef, OnDestroy, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { FilterService } from './services/filter.service';
import { TranslateService } from "@ngx-translate/core";
import { PwaService } from './services/pwa.service';
import { filter } from 'rxjs/operators'
import { UserService } from "./services/user/user.service";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ArgType } from './shared/enums/arg-type';
import { AppSetting } from "./app.setting";
import { AuthService } from "./services/auth/auth.service";
import { MetaDataService } from "./services/meta/meta-data.service";
import {AnalyticsService} from "./services/analytics/analytics.service";
import {ThemeService} from "./services/theme.service";
import { PageTabbarComponent } from './views/components/page-tabbar/page-tabbar.component';
import { PwaPopupComponent } from './views/components/pwa-popup/pwa-popup.component';
import { HeaderComponent } from './views/components/header/header.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../environments/environment';

type LegacyNavigator = Navigator & {
  browserLanguage?: string;
  userLanguage?: string;
};

type BeforeInstallPromptChoice = {
  outcome: 'accepted' | 'dismissed';
  platform: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [HeaderComponent, RouterOutlet, PwaPopupComponent, PageTabbarComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'xplorer-bowser2.0';
  showModal = signal<boolean>(false);
  popupModalTimeout?: ReturnType<typeof setTimeout>;
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  isLoadingUser = signal<boolean>(true);
  showNavigation = signal<boolean>(true);
  showHeader = signal<boolean>(true);
  isMobile = signal<boolean>(false);
  isBadUserAgent = signal<boolean>(false);
  private imageSkeletonObserver: MutationObserver | null = null;
  private beforeInstallPromptHandler?: (event: Event) => void;

  private readonly navigationHiddenRoutes: string[] = ['/login', '/signup', '/load-user', '/settings'];
  private readonly headerHiddenRoutes: string[] = ['/login', '/signup', '/load-user', '/explore'];

  private translateService = inject(TranslateService);
  private filtersService = inject(FilterService);
  private authService = inject(AuthService);
  private pwaService = inject(PwaService);
  private userService = inject(UserService);
  private router = inject(Router);
  private readonly appSetting = inject(AppSetting);
  private metaDataService = inject(MetaDataService);
  private analyticsService = inject(AnalyticsService);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  constructor() {
    if (this.isBrowser) {
      this.isMobile.set(window.innerWidth < 768);
      const userAgent = navigator.userAgent ?? '';
      this.isBadUserAgent.set(
        userAgent.includes("Instagram")
        || userAgent.includes("facebook")
        || userAgent.includes("Linkedin")
      );
    }
    this.initializeApp();
    if (this.isBrowser) {
      this.setupGlobalImageSkeletons();
    }
    this.showNavigation.set(!this.isUrlInPaths(this.router.url || '/', this.navigationHiddenRoutes));
    this.showHeader.set(!this.isUrlInPaths(this.router.url || '/', this.headerHiddenRoutes));
    this.updateMobileTabbarBodyClass();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: NavigationEnd) => {
      const currentUrl = event.urlAfterRedirects;

      this.showNavigation.set(!this.isUrlInPaths(currentUrl, this.navigationHiddenRoutes));
      this.showHeader.set(!this.isUrlInPaths(currentUrl, this.headerHiddenRoutes));
      this.updateMobileTabbarBodyClass();

      if (this.isBrowser) {
        this.checkArguments();
      }
      this.metaDataService.applyRouteMeta(currentUrl);
    });
  }

  ngOnInit(): void {
    if (this.isBrowser && !environment.IS_PRODUCTION) {
      this.cleanupServiceWorkerForNonProd();
    }

    this.authService.initializeSessionHandling();
    this.setupMetaTags();
    this.userService.initSavedXplore();
    this.isLoadingUser.set(false);

    this.analyticsService.addVisitAction();

    if (this.isBrowser && !this.isBadUserAgent()) {
      this.setupPwaInstallPromptListener();
      this.handlePwa();
    }
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }

    this.document.body.classList.remove('mobile-tabbar-visible');
    this.clearPopupModalTimeout();
    this.imageSkeletonObserver?.disconnect();
    this.imageSkeletonObserver = null;
    this.removePwaInstallPromptListener();
  }

  initializeApp() {
    if (this.isBrowser && !this.isBadUserAgent()) {
      this.initLanguage();
    }
    this.filtersService.initFilters();
    this.changeTheme();
  }

  addToHomeScreen = () => {
    if (this.isBadUserAgent() || !this.deferredPrompt) {
      return;
    }

    this.clearPopupModalTimeout();

    this.pwaService.show = false;
    void this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice
      .then((choiceResult) => {
        if (choiceResult.outcome !== 'accepted') {
          this.pwaService.setPopupDate();
        }
        this.deferredPrompt = null;
      });
  }


  handleUserResponse(response: boolean): void {
    this.showModal.set(false);
    this.clearPopupModalTimeout();
    if (!response) {
      this.pwaService.setPopupDate();
    }
  }

  private initLanguage() {
    const queryLanguage = this.getLanguageFromQuery();
    const storedLanguage = this.normalizeSupportedLanguage(localStorage.getItem('language'));
    const browserLanguage = this.normalizeSupportedLanguage(this.getUsersLocale('it'));
    const selectedLanguage = queryLanguage ?? storedLanguage ?? browserLanguage ?? 'it';

    localStorage.setItem('language', selectedLanguage);
    if (this.translateService.currentLang !== selectedLanguage) {
      this.translateService.use(selectedLanguage);
    }
    this.document.documentElement.lang = selectedLanguage;
  }

  private checkArguments(): boolean {
    if (!this.isBrowser) {
      return false;
    }
    const url = this.document.location?.href ?? '';
    const index = url.indexOf('args=');
    if (index > 0) {
      const args = url.substring(index + 5).split('&');
      return args.indexOf(ArgType.DOWNLOAD) >= 0;
    }
    return false;
  }

  private handlePwa() {
    this.pwaService.showPopup
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(status => {
        console.log(`[PWA SERVICE]: STATUS IS ${status}`);
        if (this.pwaService.canAsk() && status) {
          if (this.checkArguments()) {
            console.log('[PWA SERVICE]: IMMEDIATE DOWNLOAD');
            this.showModal.set(true);
          } else {
            console.log('[PWA SERVICE]: DEFERRED DOWNLOAD');
            this.clearPopupModalTimeout();
            this.popupModalTimeout = setTimeout(() => this.showModal.set(true), 30000);
          }
        } else {
          console.log('[PWA SERVICE]: CAN\'T ASK, USER DENIED');
        }
      });
  }

  /**
   * Checks if the current URL includes any of the specified paths.
   * @param url The current router URL.
   * @param paths An array of path strings to check against the URL.
   * @returns A boolean indicating whether any path is included in the URL.
   */
  private isUrlInPaths(url: string, paths: string[]): boolean {
    return paths.some(path => url.includes(path));
  }


  private setupMetaTags() {
    this.metaDataService.applyRouteMeta(this.router.url || '/');
  }

  private changeTheme() {
    this.themeService.changeTheme();
  }

  private updateMobileTabbarBodyClass(): void {
    if (!this.isBrowser) {
      return;
    }

    this.document.body.classList.toggle('mobile-tabbar-visible', this.showNavigation());
  }

  private getUsersLocale(defaultValue: string): string {
    const navigatorInstance = this.document.defaultView?.navigator as LegacyNavigator | undefined;
    if (!this.isBrowser || !navigatorInstance) {
      return defaultValue;
    }

    let lang = navigatorInstance.languages?.[0] || defaultValue;
    lang = lang || navigatorInstance.language || navigatorInstance.browserLanguage || navigatorInstance.userLanguage || defaultValue;

    lang = lang.toString();

    // TODO Implement a better way to handle all language
    if(lang.includes('it')) {
      return 'it';
    } else {
      return 'en';
    }
  }

  private getLanguageFromQuery(): string | null {
    if (!this.isBrowser) {
      return null;
    }

    const queryLanguage = new URLSearchParams(this.document.location?.search ?? '').get('lang');
    if (!queryLanguage) {
      return null;
    }

    const normalizedLanguage = queryLanguage.toLowerCase();
    return normalizedLanguage === 'it' || normalizedLanguage === 'en' ? normalizedLanguage : null;
  }

  private normalizeSupportedLanguage(language: string | null): 'it' | 'en' | null {
    if (!language) {
      return null;
    }

    const normalizedLanguage = language.toLowerCase().trim();
    if (normalizedLanguage.startsWith('it')) {
      return 'it';
    }
    if (normalizedLanguage.startsWith('en')) {
      return 'en';
    }

    return null;
  }

  private cleanupServiceWorkerForNonProd(): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow || !('serviceWorker' in browserWindow.navigator)) {
      return;
    }

    browserWindow.navigator.serviceWorker.getRegistrations()
      .then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      })
      .catch(() => undefined);

    if (!('caches' in browserWindow)) {
      return;
    }

    browserWindow.caches.keys()
      .then((keys) => Promise.all(keys
        .filter((key) => key.startsWith('ngsw:') || key.includes('xplorer-bowser'))
        .map((key) => browserWindow.caches.delete(key))))
      .catch(() => undefined);
  }

  private setupPwaInstallPromptListener(): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow || this.beforeInstallPromptHandler) {
      return;
    }

    this.beforeInstallPromptHandler = (event: Event) => {
      const installPromptEvent = event as BeforeInstallPromptEvent;
      event.preventDefault();
      this.deferredPrompt = installPromptEvent;
      this.pwaService.show = true;
    };

    browserWindow.addEventListener('beforeinstallprompt', this.beforeInstallPromptHandler);
  }

  private removePwaInstallPromptListener(): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow || !this.beforeInstallPromptHandler) {
      return;
    }

    browserWindow.removeEventListener('beforeinstallprompt', this.beforeInstallPromptHandler);
    this.beforeInstallPromptHandler = undefined;
  }

  private clearPopupModalTimeout(): void {
    if (!this.popupModalTimeout) {
      return;
    }

    clearTimeout(this.popupModalTimeout);
    this.popupModalTimeout = undefined;
  }

  private setupGlobalImageSkeletons(): void {
    const markImagePending = (imageElement: HTMLImageElement): void => {
      if (imageElement.classList.contains('xplorer-no-skeleton')) {
        return;
      }

      imageElement.classList.remove('xplorer-img-ready');
      imageElement.classList.add('xplorer-img-pending');
    };

    const markImageReady = (imageElement: HTMLImageElement): void => {
      imageElement.classList.remove('xplorer-img-pending');
      imageElement.classList.add('xplorer-img-ready');
    };

    const bindImage = (imageElement: HTMLImageElement): void => {
      if (imageElement.dataset['xplorerSkeletonBound'] === 'true') {
        return;
      }

      imageElement.dataset['xplorerSkeletonBound'] = 'true';
      markImagePending(imageElement);

      imageElement.addEventListener('load', () => markImageReady(imageElement));
      imageElement.addEventListener('error', () => markImageReady(imageElement));

      if (imageElement.complete && !!imageElement.currentSrc) {
        markImageReady(imageElement);
      }
    };

    const bindImagesInNode = (node: Node): void => {
      if (node instanceof HTMLImageElement) {
        bindImage(node);
        return;
      }

      if (node instanceof HTMLElement) {
        node.querySelectorAll('img').forEach((imageNode) => bindImage(imageNode));
      }
    };

    this.document.querySelectorAll('img').forEach((imageNode) => bindImage(imageNode));

    this.imageSkeletonObserver?.disconnect();
    this.imageSkeletonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
          const targetImage = mutation.target;
          markImagePending(targetImage);
          if (targetImage.complete && !!targetImage.currentSrc) {
            markImageReady(targetImage);
          }
          return;
        }

        mutation.addedNodes.forEach((addedNode) => bindImagesInNode(addedNode));
      });
    });

    this.imageSkeletonObserver.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset']
    });
  }
}
